const X_HOSTNAMES = [
  "mobile.twitter.com",
  "mobile.x.com",
  "twitter.com",
  "x.com",
] as const;

interface XPostPath {
  pathSegments: string[];
  statusIndex: number;
}

// Extract the numerical post ID required by X's official embed factory while
// rejecting profile, home, search, and lookalike-host URLs.
//
export function getXPostId(value: string): string | null {
  const postPath = getXPostPath(value);
  if (!postPath) return null;

  const postId = postPath.pathSegments[postPath.statusIndex + 1];
  return postId && /^\d{1,19}$/.test(postId) ? postId : null;
}

// Read the author segment from a canonical X status URL when it is explicit.
//
export function getXPostHandle(value: string): string | null {
  const postPath = getXPostPath(value);
  if (!postPath || postPath.statusIndex === 0) return null;

  const handle = postPath.pathSegments[postPath.statusIndex - 1];
  if (!handle || ["i", "web"].includes(handle.toLowerCase())) return null;

  return handle.replace(/^@/, "");
}

// Parse an X status path once so ID and handle extraction share validation.
//
function getXPostPath(value: string): XPostPath | null {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  if (!["http:", "https:"].includes(url.protocol)) return null;

  const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
  if (!X_HOSTNAMES.includes(hostname as (typeof X_HOSTNAMES)[number])) {
    return null;
  }

  const pathSegments = url.pathname.split("/").filter(Boolean);
  const statusIndex = pathSegments.findIndex((segment) =>
    ["status", "statuses"].includes(segment),
  );

  return statusIndex === -1 ? null : { pathSegments, statusIndex };
}
